import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Clock, LogIn, LogOut, Coffee, ArrowLeftRight, UserPlus, DollarSign, Filter,
  Pencil, Trash2, Printer, CheckCircle2, AlertTriangle, Ticket, Plus,
} from "lucide-react";

const TIPOS = [
  { v: "entrada", label: "Entrada", icon: LogIn, color: "bg-emerald-600 hover:bg-emerald-700" },
  { v: "salida_refaccion", label: "Salida Refacción", icon: Coffee, color: "bg-amber-500 hover:bg-amber-600" },
  { v: "entrada_refaccion", label: "Regreso Refacción", icon: ArrowLeftRight, color: "bg-blue-600 hover:bg-blue-700" },
  { v: "salida", label: "Salida", icon: LogOut, color: "bg-zinc-800 hover:bg-black" },
];
const TIPO_LABEL = TIPOS.reduce((a, t) => ({ ...a, [t.v]: t.label }), {});

export default function Marcaje() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [empleados, setEmpleados] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState("");
  const [estado, setEstado] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // {nombre, movimiento, hora, estado?}

  const [marcajes, setMarcajes] = useState([]);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [filterEmp, setFilterEmp] = useState("");
  const [resumen, setResumen] = useState([]);
  const [tickets, setTickets] = useState([]);

  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [form, setForm] = useState({
    nombre: "", salario_dia: 0, tolerancia_minutos: 5, penalidad_por_minuto: 0,
    extra_por_hora: 0, sancion_por_falta: 0, descuento_mala_elaboracion: 0,
    horario_entrada: "08:00", horario_salida: "17:00", minutos_refaccion: 60, activo: true,
  });

  const [tkDialog, setTkDialog] = useState({ open: false });
  const [tkForm, setTkForm] = useState({ empleado_id: "", tipo: "descuento", monto: 0, motivo: "", horas: 0, fecha: "" });

  const loadEmp = async () => {
    try { const { data } = await api.get("/empleados"); setEmpleados(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const loadEstado = async (empId) => {
    if (!empId) { setEstado(null); return; }
    try { const { data } = await api.get(`/marcajes/estado/${empId}`); setEstado(data); }
    catch (e) { setEstado(null); }
  };
  const loadMarc = async () => {
    if (!isAdmin) return;
    try {
      const params = {};
      if (filterEmp) params.empleado_id = filterEmp;
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const { data } = await api.get("/marcajes", { params });
      setMarcajes(data);
    } catch { /* silent */ }
  };
  const loadResumen = async () => {
    if (!isAdmin) return;
    try {
      const params = {};
      if (filterEmp) params.empleado_id = filterEmp;
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const { data } = await api.get("/marcajes/resumen-pago", { params });
      setResumen(data);
    } catch { /* silent */ }
  };
  const loadTickets = async () => {
    if (!isAdmin) return;
    try {
      const params = {};
      if (filterEmp) params.empleado_id = filterEmp;
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const { data } = await api.get("/tickets-empleado", { params });
      setTickets(data);
    } catch { /* silent */ }
  };

  useEffect(() => { loadEmp(); }, []);
  useEffect(() => { loadEstado(selectedEmp); }, [selectedEmp]);
  useEffect(() => { loadMarc(); loadResumen(); loadTickets(); }, [filterEmp, desde, hasta]);

  const nextAllowed = estado?.next_allowed || (estado ? [] : ["entrada"]);

  const registrar = async (tipo) => {
    if (!selectedEmp) return toast.error("Selecciona tu nombre primero");
    setBusy(true);
    try {
      const { data } = await api.post("/marcajes", { empleado_id: selectedEmp, tipo });
      const nombre = empleados.find((e) => e.id === selectedEmp)?.nombre;
      const hora = data.hora_gt || new Date(data.timestamp).toLocaleTimeString();
      setConfirm({
        nombre,
        movimiento: TIPO_LABEL[tipo] || tipo,
        hora,
        estado: data.estado, // solo para entrada
        minutos_tarde: data.minutos_tarde || 0,
      });
      loadEstado(selectedEmp);
      loadMarc();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const openNew = () => {
    setForm({
      nombre: "", salario_dia: 0, tolerancia_minutos: 5, penalidad_por_minuto: 0,
      extra_por_hora: 0, sancion_por_falta: 0, descuento_mala_elaboracion: 0,
      horario_entrada: "08:00", horario_salida: "17:00", minutos_refaccion: 60, activo: true,
    });
    setDialog({ open: true, editing: null });
  };
  const openEdit = (e) => {
    setForm({
      nombre: e.nombre, salario_dia: e.salario_dia, tolerancia_minutos: e.tolerancia_minutos,
      penalidad_por_minuto: e.penalidad_por_minuto, extra_por_hora: e.extra_por_hora,
      sancion_por_falta: e.sancion_por_falta || 0,
      descuento_mala_elaboracion: e.descuento_mala_elaboracion || 0,
      horario_entrada: e.horario_entrada || "08:00",
      horario_salida: e.horario_salida || "17:00",
      minutos_refaccion: e.minutos_refaccion ?? 60,
      activo: e.activo !== false,
    });
    setDialog({ open: true, editing: e });
  };
  const save = async () => {
    if (!form.nombre.trim()) return toast.error("Nombre requerido");
    try {
      if (dialog.editing) {
        await api.patch(`/empleados/${dialog.editing.id}`, form);
        toast.success("Empleado actualizado");
      } else {
        await api.post("/empleados", form);
        toast.success("Empleado creado");
      }
      setDialog({ open: false, editing: null });
      loadEmp();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar empleado?")) return;
    try { await api.delete(`/empleados/${id}`); toast.success("Eliminado"); loadEmp(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openTicketNew = (empleado_id = "") => {
    setTkForm({ empleado_id, tipo: "descuento", monto: 0, motivo: "", horas: 0, fecha: "" });
    setTkDialog({ open: true });
  };
  const saveTicket = async () => {
    if (!tkForm.empleado_id) return toast.error("Selecciona empleado");
    if (!(Number(tkForm.monto) > 0)) return toast.error("Monto debe ser mayor a 0");
    try {
      await api.post("/tickets-empleado", {
        empleado_id: tkForm.empleado_id,
        tipo: tkForm.tipo,
        monto: Number(tkForm.monto),
        motivo: tkForm.motivo,
        horas: tkForm.tipo === "hora_extra" ? Number(tkForm.horas || 0) : null,
        fecha: tkForm.fecha || undefined,
      });
      toast.success("Ticket creado");
      setTkDialog({ open: false });
      loadTickets(); loadResumen();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delTicket = async (tid) => {
    if (!window.confirm("¿Eliminar ticket?")) return;
    try { await api.delete(`/tickets-empleado/${tid}`); toast.success("Eliminado"); loadTickets(); loadResumen(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const imprimirRecibo = (r) => {
    const win = window.open("", "_blank", "width=560,height=800");
    if (!win) return toast.error("Habilita popups para imprimir");
    const rangoTxt = (desde || hasta) ? `Del ${desde || "inicio"} al ${hasta || "hoy"}` : "Rango completo";
    const rowsDesc = (r.tickets_descuento || []).map(t =>
      `<div class="row"><span class="label">Ticket ${t.motivo || 'descuento'} (${t.fecha})</span><span class="val neg">- Q ${Number(t.monto).toFixed(2)}</span></div>`).join("");
    const rowsExtra = (r.tickets_hora_extra || []).map(t =>
      `<div class="row"><span class="label">H. extra ${t.motivo || ''} (${t.fecha})</span><span class="val pos">+ Q ${Number(t.monto).toFixed(2)}</span></div>`).join("");
    win.document.write(`
<html><head><title>Recibo de Pago - ${r.empleado_nombre}</title>
<style>
  body { font-family: 'Courier New', monospace; padding: 24px; max-width: 520px; margin: 0 auto; }
  h1 { margin: 0 0 4px; font-size: 22px; }
  .sub { color: #666; font-size: 12px; margin-bottom: 20px; }
  .row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px dashed #ccc; }
  .row.total { border-top: 2px solid #000; border-bottom: 2px solid #000; margin-top: 12px; font-weight: 900; font-size: 18px; padding: 10px 0; }
  .row .label { text-transform: uppercase; letter-spacing: 0.06em; font-size: 11px; color: #444; }
  .row .val { font-weight: 700; }
  .footer { margin-top: 30px; font-size: 11px; color: #666; text-align: center; border-top: 1px dashed #666; padding-top: 12px; }
  .neg { color: #b91c1c; } .pos { color: #047857; }
  .sec { margin-top:14px; font-size:11px; text-transform:uppercase; letter-spacing:0.08em; color:#555; font-weight:800; }
</style></head><body>
  <h1>Recibo de Pago</h1>
  <div class="sub">${r.empleado_nombre}<br/>Turno: ${r.horario_entrada} — ${r.horario_salida} · Refacción permitida: ${r.minutos_refaccion} min<br/>${rangoTxt}<br/>Emitido: ${new Date().toLocaleString()}</div>
  <div class="row"><span class="label">Salario / día</span><span class="val">Q ${Number(r.salario_dia).toFixed(2)}</span></div>
  <div class="row"><span class="label">Días trabajados</span><span class="val">${r.dias_trabajados}</span></div>
  <div class="row"><span class="label">Días de falta</span><span class="val">${r.dias_falta}</span></div>
  <div class="row"><span class="label">Min. tarde (entrada)</span><span class="val">${Number(r.minutos_tarde).toFixed(0)}</span></div>
  <div class="row"><span class="label">Min. exceso refacción</span><span class="val">${Number(r.minutos_exceso_refaccion).toFixed(0)}</span></div>
  <div class="row"><span class="label">Base salario</span><span class="val">Q ${Number(r.base).toFixed(2)}</span></div>
  <div class="row"><span class="label">Descuento tardanzas</span><span class="val neg">- Q ${Number(r.descuento_tardanza).toFixed(2)}</span></div>
  <div class="row"><span class="label">Descuento faltas</span><span class="val neg">- Q ${Number(r.descuento_faltas).toFixed(2)}</span></div>
  <div class="row"><span class="label">Ajuste mala elaboración</span><span class="val neg">- Q ${Number(r.descuento_mala_elaboracion).toFixed(2)}</span></div>
  ${rowsDesc ? `<div class="sec">Tickets de descuento</div>${rowsDesc}` : ""}
  ${rowsExtra ? `<div class="sec">Horas extra (tickets)</div>${rowsExtra}` : ""}
  <div class="row total"><span>TOTAL A PAGAR</span><span>Q ${Number(r.neto).toFixed(2)}</span></div>
  <div class="footer">Recibí conforme el pago descrito arriba.<br/><br/>_____________________________<br/>${r.empleado_nombre}<br/>Firma</div>
  <script>window.onload = () => setTimeout(() => window.print(), 300);</script>
</body></html>`);
    win.document.close();
  };

  const currentEmp = useMemo(() => empleados.find((e) => e.id === selectedEmp), [empleados, selectedEmp]);

  return (
    <div className="space-y-6" data-testid="marcaje-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Recursos Humanos</div>
        <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
          <Clock className="w-8 h-8 text-[#F59E0B]" /> Marcaje de Asistencia
        </h1>
        <p className="text-zinc-500 mt-2 text-sm">
          Selecciona tu nombre y marca en orden: <b>Entrada → Salida de refacción → Regreso de refacción → Salida</b>.
        </p>
      </header>

      {/* Kiosco */}
      <Card className="p-6 border-zinc-200 rounded-md">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Registrar Marcaje</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label>Empleado</Label>
            <Select value={selectedEmp || "none"} onValueChange={(v) => setSelectedEmp(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="marcaje-emp-select"><SelectValue placeholder="Selecciona tu nombre" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Selecciona —</SelectItem>
                {empleados.filter((e) => e.activo !== false).map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedEmp && estado && (
              <div className="mt-4 p-3 rounded-md bg-zinc-50 border border-zinc-200">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Estado de hoy — {currentEmp?.nombre}</div>
                <div className="text-xs text-zinc-600 mb-2">Turno: <b>{estado.horario_entrada}</b> — <b>{estado.horario_salida}</b> · Refacción: <b>{estado.minutos_refaccion} min</b></div>
                {estado.marcas_hoy.length === 0 ? (
                  <div className="text-xs text-zinc-500">Aún no marcas hoy.</div>
                ) : (
                  <div className="space-y-1">
                    {estado.marcas_hoy.map((m) => (
                      <div key={m.id} className="flex items-center justify-between text-xs" data-testid={`estado-marca-${m.tipo}`}>
                        <span className="font-semibold">{m.tipo_label}</span>
                        <span className="font-mono">{m.hora_gt}</span>
                        {m.tipo === "entrada" && m.estado && (
                          <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${m.estado === "a_tiempo" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                            {m.estado === "a_tiempo" ? "A tiempo" : `Tarde ${m.minutos_tarde} min`}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {estado.last_tipo === "salida" && (
                  <div className="mt-2 text-xs text-emerald-800 font-semibold">Jornada finalizada.</div>
                )}
              </div>
            )}
          </div>

          <div>
            <Label>Próximo marcaje permitido</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {TIPOS.map((t) => {
                const enabled = !!selectedEmp && nextAllowed.includes(t.v);
                return (
                  <Button
                    key={t.v}
                    disabled={busy || !enabled}
                    onClick={() => registrar(t.v)}
                    className={`${enabled ? t.color : "bg-zinc-200 text-zinc-500"} text-white gap-2 h-20 text-base font-semibold`}
                    data-testid={`marcaje-btn-${t.v}`}
                  >
                    <t.icon className="w-5 h-5" /> {t.label}
                  </Button>
                );
              })}
            </div>
            {selectedEmp && nextAllowed.length === 0 && (
              <div className="mt-2 text-xs text-zinc-500">No hay marcas pendientes por hoy.</div>
            )}
          </div>
        </div>
      </Card>

      {/* Admin */}
      {isAdmin && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="p-5 border-zinc-200 rounded-md lg:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Empleados</div>
                  <div className="text-xl font-semibold">Base de datos</div>
                </div>
                <Dialog open={dialog.open} onOpenChange={(v) => setDialog({ ...dialog, open: v })}>
                  <DialogTrigger asChild>
                    <Button className="gap-2 bg-[#002FA7] hover:bg-[#00227A]" onClick={openNew} data-testid="empleado-new-btn">
                      <UserPlus className="w-4 h-4" /> Nuevo empleado
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>{dialog.editing ? "Editar empleado" : "Nuevo empleado"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                      <div><Label>Nombre</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="emp-form-nombre" /></div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label>Turno: Entrada</Label><Input type="time" value={form.horario_entrada} onChange={(e) => setForm({ ...form, horario_entrada: e.target.value })} data-testid="emp-form-entrada" /></div>
                        <div><Label>Turno: Salida</Label><Input type="time" value={form.horario_salida} onChange={(e) => setForm({ ...form, horario_salida: e.target.value })} data-testid="emp-form-salida" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label>Salario/día (Q)</Label><Input type="number" step="0.01" value={form.salario_dia} onChange={(e) => setForm({ ...form, salario_dia: Number(e.target.value) })} data-testid="emp-form-salario" /></div>
                        <div><Label>Tolerancia entrada (min)</Label><Input type="number" value={form.tolerancia_minutos} onChange={(e) => setForm({ ...form, tolerancia_minutos: Number(e.target.value) })} data-testid="emp-form-tolerancia" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label>Refacción permitida (min)</Label><Input type="number" value={form.minutos_refaccion} onChange={(e) => setForm({ ...form, minutos_refaccion: Number(e.target.value) })} data-testid="emp-form-refaccion" /></div>
                        <div><Label>Sanción por tardanza (Q/min)</Label><Input type="number" step="0.01" value={form.penalidad_por_minuto} onChange={(e) => setForm({ ...form, penalidad_por_minuto: Number(e.target.value) })} data-testid="emp-form-qmin" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label>Sanción por falta (Q/día)</Label><Input type="number" step="0.01" value={form.sancion_por_falta} onChange={(e) => setForm({ ...form, sancion_por_falta: Number(e.target.value) })} data-testid="emp-form-sancion" /></div>
                        <div><Label>Q por hora extra (referencia)</Label><Input type="number" step="0.01" value={form.extra_por_hora} onChange={(e) => setForm({ ...form, extra_por_hora: Number(e.target.value) })} data-testid="emp-form-qhora" /></div>
                      </div>
                      <div><Label>Descuento fijo por mala elaboración (Q)</Label><Input type="number" step="0.01" value={form.descuento_mala_elaboracion} onChange={(e) => setForm({ ...form, descuento_mala_elaboracion: Number(e.target.value) })} data-testid="emp-form-mala" /></div>
                      <div className="flex items-center gap-2"><input type="checkbox" id="emp-activo" checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} /><Label htmlFor="emp-activo">Activo</Label></div>
                    </div>
                    <DialogFooter>
                      <Button onClick={save} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="emp-form-save">Guardar</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="overflow-x-auto -mx-5">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-zinc-600 text-left">
                    <tr>
                      <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Nombre</th>
                      <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Turno</th>
                      <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Salario/día</th>
                      <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Toler.</th>
                      <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Refac.</th>
                      <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Q/min</th>
                      <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {empleados.map((e) => (
                      <tr key={e.id} className="hover:bg-zinc-50" data-testid={`emp-row-${e.nombre}`}>
                        <td className="px-4 py-2 font-semibold">{e.nombre}{e.activo === false && <span className="ml-2 text-[10px] text-red-600 uppercase">Inactivo</span>}</td>
                        <td className="px-4 py-2 text-xs text-zinc-600">{e.horario_entrada || "—"} – {e.horario_salida || "—"}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{formatMoney(e.salario_dia)}</td>
                        <td className="px-4 py-2 text-right">{e.tolerancia_minutos} m</td>
                        <td className="px-4 py-2 text-right">{e.minutos_refaccion ?? 60} m</td>
                        <td className="px-4 py-2 text-right tabular-nums text-red-700">{formatMoney(e.penalidad_por_minuto)}</td>
                        <td className="px-4 py-2 text-right space-x-1">
                          <Button size="sm" variant="ghost" onClick={() => openTicketNew(e.id)} title="Nuevo ticket"><Ticket className="w-3 h-3 text-[#002FA7]" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(e)}><Pencil className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => del(e.id)}><Trash2 className="w-3 h-3 text-red-600" /></Button>
                        </td>
                      </tr>
                    ))}
                    {empleados.length === 0 && <tr><td colSpan={7} className="text-center text-zinc-400 py-6">Sin empleados</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>
            <Card className="p-5 border-zinc-200 rounded-md">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2"><Filter className="w-3 h-3" /> Filtros de rango</div>
              <div className="mt-3 space-y-2">
                <div>
                  <Label>Empleado</Label>
                  <Select value={filterEmp || "all"} onValueChange={(v) => setFilterEmp(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="marcaje-filter-emp"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {empleados.map((e) => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Desde</Label><Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="marcaje-desde" /></div>
                  <div><Label>Hasta</Label><Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="marcaje-hasta" /></div>
                </div>
                <Button className="w-full gap-2 bg-[#002FA7] hover:bg-[#00227A]" onClick={() => openTicketNew()} data-testid="ticket-new-btn">
                  <Plus className="w-4 h-4" /> Nuevo ticket (descuento / hora extra)
                </Button>
              </div>
            </Card>
          </div>

          {/* Tickets */}
          <Card className="p-5 border-zinc-200 rounded-md">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2 flex items-center gap-1">
              <Ticket className="w-3 h-3" /> Tickets de descuento / horas extra
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-600 text-left">
                  <tr>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Fecha</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Empleado</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Tipo</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Motivo</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Monto</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {tickets.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-400 py-4">Sin tickets</td></tr>}
                  {tickets.map((t) => (
                    <tr key={t.id} className="hover:bg-zinc-50" data-testid={`ticket-row-${t.id}`}>
                      <td className="px-4 py-2 font-mono text-xs">{t.fecha}</td>
                      <td className="px-4 py-2 font-semibold">{t.empleado_nombre}</td>
                      <td className="px-4 py-2 text-xs uppercase font-bold">
                        {t.tipo === "descuento"
                          ? <span className="text-red-700">Descuento</span>
                          : <span className="text-emerald-700">Hora extra{t.horas ? ` (${t.horas}h)` : ""}</span>}
                      </td>
                      <td className="px-4 py-2 text-xs">{t.motivo || "—"}</td>
                      <td className={`px-4 py-2 text-right tabular-nums font-bold ${t.tipo === "descuento" ? "text-red-700" : "text-emerald-700"}`}>
                        {t.tipo === "descuento" ? "-" : "+"} {formatMoney(t.monto)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => delTicket(t.id)}><Trash2 className="w-3 h-3 text-red-600" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Resumen pago */}
          <Card className="p-5 border-zinc-200 rounded-md">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2 flex items-center gap-1">
              <DollarSign className="w-3 h-3" /> Resumen de Pago & Recibos
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-600 text-left">
                  <tr>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Empleado</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Días</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Faltas</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Min tarde</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Exc. refac.</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Base</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Descuento</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Extra</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Neto</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Recibo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {resumen.length === 0 && <tr><td colSpan={10} className="text-center text-zinc-400 py-4">Sin datos</td></tr>}
                  {resumen.map((r) => (
                    <tr key={r.empleado_id} className="hover:bg-zinc-50" data-testid={`pago-${r.empleado_id}`}>
                      <td className="px-4 py-2 font-semibold">{r.empleado_nombre}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.dias_trabajados}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.dias_falta}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{Number(r.minutos_tarde).toFixed(0)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{Number(r.minutos_exceso_refaccion).toFixed(0)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatMoney(r.base)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-red-700">{formatMoney(r.descuento)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-emerald-700">{formatMoney(r.extra)}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-bold">{formatMoney(r.neto)}</td>
                      <td className="px-4 py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => imprimirRecibo(r)} className="gap-1" data-testid={`imprimir-recibo-${r.empleado_id}`}>
                          <Printer className="w-3 h-3" /> Imprimir
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Marcajes recientes */}
          <Card className="p-5 border-zinc-200 rounded-md">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Marcajes recientes</div>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-600 text-left sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Fecha / Hora</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Empleado</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Tipo</th>
                    <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {marcajes.slice(0, 100).map((m) => (
                    <tr key={m.id} className="hover:bg-zinc-50">
                      <td className="px-4 py-2 text-xs font-mono">{m.fecha} {m.hora_gt || (m.timestamp || "").slice(11, 19)}</td>
                      <td className="px-4 py-2">{m.empleado_nombre}</td>
                      <td className="px-4 py-2 text-xs uppercase font-semibold">{TIPO_LABEL[m.tipo] || m.tipo}</td>
                      <td className="px-4 py-2 text-xs">
                        {m.tipo === "entrada" && m.estado && (
                          <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${m.estado === "a_tiempo" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                            {m.estado === "a_tiempo" ? "A tiempo" : `Tarde ${m.minutos_tarde} min`}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {marcajes.length === 0 && <tr><td colSpan={4} className="text-center text-zinc-400 py-4">Sin marcajes</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* Confirmación de marcaje */}
      <Dialog open={!!confirm} onOpenChange={(v) => { if (!v) setConfirm(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" /> Marcaje registrado
          </DialogTitle></DialogHeader>
          {confirm && (
            <div className="text-center py-4" data-testid="marcaje-confirm">
              <div className="text-2xl font-black mb-1">{confirm.nombre}</div>
              <div className="text-sm uppercase tracking-widest text-zinc-500 font-semibold">{confirm.movimiento}</div>
              <div className="text-4xl font-black tabular-nums mt-3">{confirm.hora}</div>
              {confirm.estado && (
                <div className={`mt-3 inline-flex items-center gap-1 text-xs uppercase font-bold px-3 py-1 rounded ${confirm.estado === "a_tiempo" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                  {confirm.estado === "a_tiempo"
                    ? <>A tiempo</>
                    : <><AlertTriangle className="w-3 h-3" /> Tarde {confirm.minutos_tarde} min</>}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setConfirm(null)} className="w-full bg-[#002FA7] hover:bg-[#00227A]" data-testid="marcaje-confirm-close">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nuevo Ticket */}
      <Dialog open={tkDialog.open} onOpenChange={(v) => setTkDialog({ open: v })}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuevo Ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Empleado</Label>
              <Select value={tkForm.empleado_id || "none"} onValueChange={(v) => setTkForm({ ...tkForm, empleado_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="ticket-emp-select"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Selecciona —</SelectItem>
                  {empleados.map((e) => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Tipo</Label>
                <Select value={tkForm.tipo} onValueChange={(v) => setTkForm({ ...tkForm, tipo: v })}>
                  <SelectTrigger data-testid="ticket-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="descuento">Descuento (Q)</SelectItem>
                    <SelectItem value="hora_extra">Hora extra (+Q)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Fecha</Label><Input type="date" value={tkForm.fecha} onChange={(e) => setTkForm({ ...tkForm, fecha: e.target.value })} data-testid="ticket-fecha" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Monto Q</Label><Input type="number" step="0.01" value={tkForm.monto} onChange={(e) => setTkForm({ ...tkForm, monto: e.target.value })} data-testid="ticket-monto" /></div>
              {tkForm.tipo === "hora_extra" && (
                <div><Label>Horas (referencia)</Label><Input type="number" step="0.5" value={tkForm.horas} onChange={(e) => setTkForm({ ...tkForm, horas: e.target.value })} data-testid="ticket-horas" /></div>
              )}
            </div>
            <div><Label>Motivo</Label><Input value={tkForm.motivo} onChange={(e) => setTkForm({ ...tkForm, motivo: e.target.value })} placeholder="Ej: mala elaboración, hrs extra sábado…" data-testid="ticket-motivo" /></div>
          </div>
          <DialogFooter>
            <Button onClick={saveTicket} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="ticket-save">Guardar ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
